import { LinearProgress } from "@material-ui/core";
import produce from "immer";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useGetExamSessionQuery, useUpdateExamAndExpertAssignmentsMutation } from "../../../api/rtk/ExamSessionApi";
import {
  useAssignCandidatesAndExpertsMutation,
  useGetExamSessionCandidatesQuery,
} from "../../../api/rtk/ExamSessionCandidateApi";
import { useGetTimeSlotStructureQuery } from "../../../api/rtk/TimeSlotStructureApi";
import { ExamDayType } from "../../../model/ExamDayType";
import { ExamDayViewModel } from "../../../model/ExamDayViewModel";
import { ExamSessionCandidateExamState } from "../../../model/examSessionCandidate/ExamSessionCandidateExamViewModel";
import { GetLocalizedName } from "../../../model/ExamSessionViewModel";
import { RoomViewModel } from "../../../model/RoomViewModel";
import { TimeSlotStructure } from "../../../model/TimeSlotStructure";
import { routesPaths } from "../../../Routes";
import {
  SchedulerCandidate,
  SchedulerCandidateContext,
  SchedulerCandidateContextValue,
  SchedulerExamCandidate,
} from "./SchedulerCandidateContext";
import {
  ExamCandidateCaseName,
  SchedulerCaseNameContext,
  SchedulerCaseNameContextValue,
} from "./SchedulerCaseNameContext";
import { SchedulerExpertContext, SchedulerRoomExperts, SchedulerExpertContextValue } from "./SchedulerExpertContext";
import { SchedulerContext, SchedulerContextValue } from "./SchedulerContext";
import {
  addTimeSlotExam,
  areRoomsNotOverloaded,
  clearTimeSlotExams,
  getAssignCandidatesAndExpertsRequestModel,
  getCandidatesContextData,
  getExamAndExpertsAssignmentRequestModels,
  getExamDay,
  getExamDays,
  getExamTypes,
  getLocation,
  isOralDay,
  removeTimeSlotExam,
  setAllTimeSlotsExams,
} from "./SchedulerContextUtils";
import { ExpertAssignmentRequestModel } from "../../../model/ExpertAssignmentRequestModel";
import { AssignedUserViewModel } from "../../../model/AssignedUserViewModel";

const updateExamDayExperts = (
  schedulerExperts: SchedulerRoomExperts,
  roomId: string,
  areFixed: boolean,
  assignedExperts: AssignedUserViewModel[]
): SchedulerRoomExperts => {
  return {
    examDayId: schedulerExperts.examDayId,
    expertAssignments: schedulerExperts.expertAssignments.map((expertAssignment) =>
      expertAssignment.roomId === roomId
        ? updateRoomExperts(expertAssignment, areFixed, assignedExperts)
        : expertAssignment
    ),
  };
};

const updateRoomExperts = (
  expertAssignment: ExpertAssignmentRequestModel,
  areFixed: boolean,
  assignedExperts: AssignedUserViewModel[]
): ExpertAssignmentRequestModel => {
  return {
    roomId: expertAssignment.roomId,
    assignedUsers: expertAssignment.assignedUsers
      .filter((assignedExpert) => assignedExpert.isFixed !== areFixed)
      .concat(assignedExperts),
  };
};

interface SchedulerContextProviderProps {
  examDayType: ExamDayType;
  readonly?: boolean;
}

export const SchedulerContextProvider: FC<SchedulerContextProviderProps> = ({ examDayType, readonly, children }) => {
  const history = useHistory();
  const { examSessionId, locationId } = useParams<{ examSessionId: string; locationId: string }>();
  const { data: examSession, isFetching: isExamSessionFetching } = useGetExamSessionQuery(examSessionId, {
    refetchOnMountOrArgChange: true,
  });
  const { data: examSessionCandidates, isFetching: areExamSessionCandidatesFetching } =
    useGetExamSessionCandidatesQuery(examSessionId);

  const { data: timeSlotStructure, isFetching: isTimeSlotStructureFetching } = useGetTimeSlotStructureQuery();
  const [updateExamAndExpertAssignments, { isLoading: isUpdateExamAndExpertAssignmentsLoading }] =
    useUpdateExamAndExpertAssignmentsMutation();
  const [assignCandidatesAndExperts, { isLoading: isAssignCandidatesAndExpertsLoading }] =
    useAssignCandidatesAndExpertsMutation();

  const [selectedExamDayId, setSelectedExamDayId] = useState("");
  const [examDays, setExamDays] = useState<ExamDayViewModel[]>([]);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [schedulerCandidates, setSchedulerCandidates] = useState<SchedulerCandidate[]>([]);
  const [schedulerExperts, setSchedulerExperts] = useState<SchedulerRoomExperts[]>([]);
  const [schedulerExamCandidates, setSchedulerExamCandidates] = useState<SchedulerExamCandidate[]>([]);
  const [examCandidateCaseNames, setExamCandidateCaseNames] = useState<ExamCandidateCaseName[]>([]);

  const isSessionPublished = examSession?.published || false;
  const selectedExamDay = useMemo(() => getExamDay(examDays, selectedExamDayId), [examDays, selectedExamDayId]);
  const examTypes = getExamTypes(selectedExamDay);
  const location = useMemo(() => getLocation(examSession, locationId), [examSession, locationId]);
  const exams = useMemo(
    () => examSession?.exams.filter((exam) => examTypes.includes(exam.type)) || [],
    [examSession, examTypes]
  );
  const rooms = useMemo(() => {
    if (selectedExamDay === undefined || location === undefined) {
      return [];
    }

    var preparationRooms: RoomViewModel[] = [];
    var examRooms: RoomViewModel[] = [];
    selectedExamDay.roomAssignments.forEach((roomAssignment) => {
      var room = location.rooms.find((room) => room.id === roomAssignment.roomId);
      if (room !== undefined) {
        roomAssignment.isPreparationRoom ? preparationRooms.push(room) : examRooms.push(room);
      }
    });
    return [...preparationRooms, ...examRooms];
  }, [location, selectedExamDay]);

  const updateCaseNames = useCallback(
    (examCandidateCaseNames: ExamCandidateCaseName[]) => {
      if (!isOralDay(examTypes)) {
        return;
      }

      setExamCandidateCaseNames((prevCaseNames) =>
        produce(prevCaseNames, (draftCaseNames) => {
          examCandidateCaseNames.forEach(({ candidateId, examId, caseName }) => {
            var schedulerExamCandidates = draftCaseNames.filter(
              (examCandidate) => examCandidate.candidateId === candidateId && examCandidate.examId === examId
            );
            schedulerExamCandidates.forEach((examSessionCandidate) => {
              examSessionCandidate.caseName = caseName;
            });
          });
        })
      );
    },
    [examTypes]
  );

  const updateCaseName = useCallback(
    (candidateId: string, examId: string, caseName: string) => {
      updateCaseNames([{ candidateId, examId, caseName }]);
    },
    [updateCaseNames]
  );

  const schedulerCaseNameContext = useMemo<SchedulerCaseNameContext>(
    () => ({
      examCandidateCaseNames,
      updateCaseName,
    }),
    [examCandidateCaseNames, updateCaseName]
  );

  const updateSchedulerExamCandidate = useCallback(
    (
      schedulerExamCandidatesFilter: (schedulerExamCandidate: SchedulerExamCandidate) => boolean,
      updateData: Partial<SchedulerExamCandidate>
    ) => {
      setSchedulerExamCandidates((prevCandidates) => {
        let caseNamesToReset: ExamCandidateCaseName[] = [];
        var newContext = produce(prevCandidates, (draftCandidates) => {
          const examCandidates = draftCandidates.filter(schedulerExamCandidatesFilter);
          caseNamesToReset = examCandidates.map(({ candidateId, examId }) => ({
            candidateId,
            examId,
            caseName: "",
          }));
          examCandidates.forEach((examCandidate) => {
            Object.assign(examCandidate, updateData);
          });
        });
        updateCaseNames(caseNamesToReset);
        return newContext;
      });
    },
    [updateCaseNames]
  );

  const scheduleExam = useCallback(
    (candidateId: string, examId: string, examDayId: string, roomId: string, timeSlotIndex: number) =>
      updateSchedulerExamCandidate(
        (schedulerExamCandidate) =>
          schedulerExamCandidate.candidateId === candidateId && schedulerExamCandidate.examId === examId,
        {
          examDayId,
          roomId,
          timeSlotIndex,
          examState: roomId === "" ? ExamSessionCandidateExamState.Cancelled : ExamSessionCandidateExamState.Booked,
        }
      ),
    [updateSchedulerExamCandidate]
  );

  const addCandidate = useCallback(
    (candidateId: string, examId: string, examDayId: string, roomId: string, timeSlotIndex: number) =>
      scheduleExam(candidateId, examId, examDayId, roomId, timeSlotIndex),
    [scheduleExam]
  );

  const removeCandidate = useCallback(
    (candidateId: string, examId: string) => scheduleExam(candidateId, examId, "", "", 0),
    [scheduleExam]
  );

  const clearCandidates = useCallback(
    (examDayId: string, roomId: string, timeSlotIndex: number) =>
      updateSchedulerExamCandidate(
        (schedulerExamCandidate) =>
          schedulerExamCandidate.examDayId === examDayId &&
          schedulerExamCandidate.roomId === roomId &&
          schedulerExamCandidate.timeSlotIndex === timeSlotIndex,
        {
          examState: ExamSessionCandidateExamState.Cancelled,
          examDayId: "",
          roomId: "",
          timeSlotIndex: 0,
        }
      ),
    [updateSchedulerExamCandidate]
  );

  const changeExpert = useCallback(
    (examDayId: string, roomId: string) => (areFixed: boolean, selectedExperts: AssignedUserViewModel[]) => {
      setSchedulerExperts((prevSchedulerExperts) =>
        [...prevSchedulerExperts].map((schedulerExpert) =>
          schedulerExpert.examDayId === examDayId
            ? updateExamDayExperts(schedulerExpert, roomId, areFixed, selectedExperts)
            : schedulerExpert
        )
      );
    },
    []
  );

  const examSessionCandidatesContext = useMemo<SchedulerCandidateContext>(
    () => ({
      isValid,
      schedulerCandidates,
      schedulerExamCandidates,
      addCandidate,
      removeCandidate,
      clearCandidates,
    }),
    [isValid, schedulerCandidates, schedulerExamCandidates, addCandidate, removeCandidate, clearCandidates]
  );

  const examSessionExpertsContext = useMemo<SchedulerExpertContext>(
    () => ({
      schedulerExperts,
      changeExpert,
    }),
    [changeExpert, schedulerExperts]
  );

  const handleAddTimeSlotExam = useCallback(
    (roomId: string, examId: string, timeSlotIndex: number) =>
      setExamDays((prevExamDays) =>
        produce(prevExamDays, (draftExamDays) =>
          addTimeSlotExam(draftExamDays, selectedExamDayId, roomId, examId, timeSlotIndex)
        )
      ),
    [selectedExamDayId]
  );

  const handleRemoveTimeSlotExam = useCallback(
    (roomId: string, examId: string, timeSlotIndex: number) =>
      setExamDays((prevExamDays) =>
        produce(prevExamDays, (draftExamDays) =>
          removeTimeSlotExam(draftExamDays, selectedExamDayId, roomId, examId, timeSlotIndex)
        )
      ),
    [selectedExamDayId]
  );

  const handleClearTimeSlotExams = useCallback(
    (roomId: string, timeSlotIndex: number) =>
      setExamDays((prevExamDays) =>
        produce(prevExamDays, (draftExamDays) =>
          clearTimeSlotExams(draftExamDays, selectedExamDayId, roomId, timeSlotIndex)
        )
      ),
    [selectedExamDayId]
  );

  const handleSetAllTimeSlotsExams = useCallback(
    (roomId: string, examIds: string[]) => {
      if (timeSlotStructure === undefined) {
        return;
      }

      setExamDays((prevExamDays) =>
        produce(prevExamDays, (draftExamDays) =>
          setAllTimeSlotsExams(draftExamDays, selectedExamDayId, timeSlotStructure, roomId, examIds)
        )
      );
    },
    [selectedExamDayId, timeSlotStructure]
  );

  const changeSelectedExamDay = useCallback((examDayId: string) => {
    setSelectedExamDayId(examDayId);
  }, []);

  const navigateToSession = useCallback(() => {
    const route = routesPaths.examSessionLocation(examSessionId, locationId);
    history.push(route);
  }, [examSessionId, locationId, history]);

  const save = useCallback(async () => {
    const saveCandidateAndExpertsAssignments = () => {
      const requestModel = getAssignCandidatesAndExpertsRequestModel(
        schedulerExamCandidates,
        examCandidateCaseNames,
        examDays,
        schedulerExperts,
        examSessionId,
        examSessionCandidates
      );

      return assignCandidatesAndExperts(requestModel);
    };

    const saveExamAndExpertsAssignments = () => {
      const examAndExpertAssignmentsRequestModel = getExamAndExpertsAssignmentRequestModels(examDays, schedulerExperts);
      return updateExamAndExpertAssignments({
        examSessionId,
        data: examAndExpertAssignmentsRequestModel,
      });
    };

    if (isSessionPublished && isValid) {
      const response = await saveCandidateAndExpertsAssignments().unwrap();
      if (response.success) {
        navigateToSession();
      }
    }

    if (!isSessionPublished) {
      const response = await saveExamAndExpertsAssignments().unwrap();
      if (response.success) {
        navigateToSession();
      }
    }
  }, [
    isSessionPublished,
    isValid,
    schedulerExamCandidates,
    examCandidateCaseNames,
    examDays,
    schedulerExperts,
    examSessionCandidates,
    assignCandidatesAndExperts,
    updateExamAndExpertAssignments,
    examSessionId,
    navigateToSession,
  ]);

  const schedulerContext = useMemo<SchedulerContext>(() => {
    return {
      examTypes,
      readonly: readonly === true,
      examSession: examSession,
      examSessionLabel: examSession !== undefined ? GetLocalizedName(examSession) : "",
      selectedLocationName: location !== undefined ? location.name : "",
      isSessionPublished,
      selectedExamDayId,
      locations: examSession !== undefined ? examSession.locations : [],
      examDays,
      rooms,
      exams,
      timeSlotStructure: timeSlotStructure !== undefined ? timeSlotStructure : ({} as TimeSlotStructure),
      addTimeSlotExam: handleAddTimeSlotExam,
      removeTimeSlotExam: handleRemoveTimeSlotExam,
      clearTimeSlotExams: handleClearTimeSlotExams,
      setAllTimeSlotsExams: handleSetAllTimeSlotsExams,
      changeSelectedExamDay,
      save,
      navigateToSession,
    };
  }, [
    examTypes,
    readonly,
    examSession,
    location,
    isSessionPublished,
    selectedExamDayId,
    examDays,
    rooms,
    exams,
    timeSlotStructure,
    handleAddTimeSlotExam,
    handleRemoveTimeSlotExam,
    handleClearTimeSlotExams,
    handleSetAllTimeSlotsExams,
    changeSelectedExamDay,
    save,
    navigateToSession,
  ]);

  // Update isValid
  useEffect(() => {
    const allAssigned = schedulerExamCandidates.every(
      (examCandidate) => examCandidate.examState !== ExamSessionCandidateExamState.Cancelled
    );
    const valid = allAssigned && areRoomsNotOverloaded(examSession, locationId, schedulerExamCandidates);
    setIsValid(valid);
  }, [examSession, examDays, locationId, schedulerExamCandidates]);

  useEffect(() => {
    const examDays = getExamDays(examSession, locationId, examDayType);
    if (examDays === undefined) {
      return;
    }

    setExamDays(examDays);
    if (examDays[0] !== undefined) {
      setSelectedExamDayId(examDays[0].id);
    }
  }, [examSession, locationId, examDayType]);

  useEffect(() => {
    if (examSessionCandidates === undefined) {
      return;
    }

    const { schedulerCandidates, schedulerExamCandidates, examCandidateCaseNames } = getCandidatesContextData(
      examSessionCandidates,
      examDays
    );
    setSchedulerCandidates(schedulerCandidates);
    setSchedulerExamCandidates(schedulerExamCandidates);
    setExamCandidateCaseNames(examCandidateCaseNames);
  }, [examSessionCandidates, examDays]);

  useEffect(() => {
    if (examDays === undefined) {
      return;
    }
    setSchedulerExperts(
      examDays.map((ed) => ({
        examDayId: ed.id,
        expertAssignments: ed.roomAssignments.map((ra) => ({
          roomId: ra.roomId,
          assignedUsers: ra.assignedUsers.map((user) => ({ isFixed: user.isFixed, userId: user.userId })),
        })),
      }))
    );
  }, [examDays]);

  if (
    isExamSessionFetching ||
    areExamSessionCandidatesFetching ||
    isUpdateExamAndExpertAssignmentsLoading ||
    isAssignCandidatesAndExpertsLoading ||
    isTimeSlotStructureFetching
  ) {
    return <LinearProgress variant="query" />;
  }

  return (
    <SchedulerContextValue.Provider value={schedulerContext}>
      <SchedulerCandidateContextValue.Provider value={examSessionCandidatesContext}>
        <SchedulerExpertContextValue.Provider value={examSessionExpertsContext}>
          <SchedulerCaseNameContextValue.Provider value={schedulerCaseNameContext}>
            {children}
          </SchedulerCaseNameContextValue.Provider>
        </SchedulerExpertContextValue.Provider>
      </SchedulerCandidateContextValue.Provider>
    </SchedulerContextValue.Provider>
  );
};

export default SchedulerContextProvider;
